"use client";

import {
  InventorySkuStockBanner,
  InventoryStockHero,
  InventoryStockLoading,
} from "@/modules/merchant/presentation/ui/InventorySkuStockBanner";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  Loader,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import {
  shopInventoryApi,
  type ShopBranchOption,
  type ShopInventoryStockRow,
} from "../api/client";
import {
  useAdjustShopInventoryMutation,
  useShopInventoryBranchSkuStock,
} from "../api/queries";

const FormSchema = z.object({
  branchId: z.string().uuid("ເລືອກສາຂາ"),
  delta: z.coerce
    .number()
    .int()
    .refine((n) => n !== 0, "ຕ້ອງບໍ່ເປັນ 0"),
  reason: z.string().min(1, "ຕ້ອງໃສ່ເຫດຜົນ").max(2000),
  adjustmentType: z.enum(["count", "damage", "correction"]),
});

type FormValues = z.infer<typeof FormSchema>;

const ADJUSTMENT_OPTIONS = [
  { label: "ນັບສຕ໋ອກ", value: "count" },
  { label: "ເສຍຫາຍ", value: "damage" },
  { label: "ຮັບຜິດ", value: "correction" },
];

const REASON_PRESETS: {
  label: string;
  adjustmentType: FormValues["adjustmentType"];
  reason: string;
}[] = [
  { label: "ນັບສຕ໋ອກ", adjustmentType: "count", reason: "ນັບສຕ໋ອກ" },
  { label: "ເສຍຫາຍ", adjustmentType: "damage", reason: "ເສຍຫາຍ" },
  { label: "ຮັບຜິດ", adjustmentType: "correction", reason: "ຮັບຜິດ" },
];

type ShopInventoryAdjustModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  row: ShopInventoryStockRow | null;
  onSuccess?: () => void;
};

export function ShopInventoryAdjustModal({
  open,
  onOpenChange,
  shopId,
  row,
  onSuccess,
}: ShopInventoryAdjustModalProps) {
  const adjust = useAdjustShopInventoryMutation(shopId);

  const form = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema) as Resolver<FormValues>,
    defaultValues: {
      branchId: "",
      delta: 0,
      reason: "",
      adjustmentType: "count",
    },
  });

  useEffect(() => {
    if (open && row) {
      form.reset({
        branchId: row.branchId ?? "",
        delta: 0,
        reason: "",
        adjustmentType: "count",
      });
    }
  }, [open, row, form]);

  const onSubmit = async (vals: FormValues) => {
    if (!row) return;
    try {
      await adjust.mutateAsync({
        shopProductSkuId: row.shopProductSkuId,
        branchId: vals.branchId,
        delta: vals.delta,
        reason: vals.reason.trim(),
        adjustmentType: vals.adjustmentType,
      });
      form.reset({
        branchId: row.branchId ?? "",
        delta: 0,
        reason: "",
        adjustmentType: "count",
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // toast ຈາກ mutation
    }
  };

  const branchLine =
    row?.branchName != null
      ? `ສາຂາ: ${row.branchName}`
      : row?.branchId != null
        ? `ສາຂາ: ${row.branchId}`
        : null;

  const selectedBranchId =
    (row?.branchId ?? form.watch("branchId")?.trim()) || undefined;

  const stockQuery = useShopInventoryBranchSkuStock(shopId, {
    branchId: selectedBranchId,
    shopProductSkuId: row?.shopProductSkuId,
    enabled: open && !!row,
  });
  const stockLine = stockQuery.data?.data[0];
  const displayOnHand =
    stockLine?.quantityOnHand != null
      ? String(stockLine.quantityOnHand)
      : stockLine && !stockLine.inventoryTracked
        ? "0"
        : "—";
  const showStockHint =
    !row?.branchId && (!selectedBranchId || selectedBranchId === "");

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="ປັບສະຕ໋ອກ" size="md">
      {row ? (
        <FormRoot<FormValues>
          methods={form}
          onSubmit={(v) => onSubmit(v)}
          className="space-y-4"
        >
          {row.branchId ? (
            <></>
          ) : (
            <div className="space-y-2">
              <Label>ສາຂາ</Label>
              <InfiniteCombobox<ShopBranchOption>
                queryKey={[
                  "shops",
                  shopId,
                  "inventory-adjust",
                  "branch-lookup",
                ]}
                queryFn={async ({ search: q, pageParam }) => {
                  return shopInventoryApi.lookupBranches(shopId, {
                    page: pageParam,
                    search: q.trim() || undefined,
                    limit: 20,
                  });
                }}
                preloadQueryFn={(id) => shopInventoryApi.getBranch(shopId, id)}
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={form.watch("branchId")}
                onValueChange={(val) =>
                  form.setValue("branchId", val, { shouldValidate: true })
                }
                placeholder="ເລືອກສາຂາ..."
                className="h-9 w-full"
                clearable
              />
              {form.formState.errors.branchId ? (
                <p className="text-destructive text-xs">
                  {form.formState.errors.branchId.message}
                </p>
              ) : null}
            </div>
          )}
          <RHF.Controller
            name="branchId"
            control={form.control}
            render={({ field }) =>
              row.branchId ? (
                <input type="hidden" {...field} value={row.branchId} />
              ) : (
                <input type="hidden" {...field} />
              )
            }
          />

          <InventorySkuStockBanner
            productName={row.productName}
            shopSku={row.shopSku}
            metaLines={branchLine ? [branchLine] : undefined}
            stockArea={
              showStockHint ? (
                <p className="text-muted-foreground text-sm sm:text-right">
                  ເລືອກສາຂາເພື່ອເຫັນຍອດຄົງເຫຼືອ
                </p>
              ) : stockQuery.isLoading ? (
                <InventoryStockLoading />
              ) : (
                <>
                  <InventoryStockHero value={displayOnHand} />
                  {stockLine != null &&
                  stockLine.quantityReserved != null &&
                  stockLine.quantityReserved > 0 ? (
                    <p className="mt-1 text-muted-foreground text-xs sm:text-right">
                      ຈອງ:{" "}
                      <span className="tabular-nums">
                        {stockLine.quantityReserved}
                      </span>
                    </p>
                  ) : null}
                  {stockLine != null &&
                  stockLine.quantityOnHand == null &&
                  !stockLine.inventoryTracked ? (
                    <p className="mt-1 text-muted-foreground text-xs leading-snug sm:text-right">
                      ຍັງບໍ່ມີແຖວສະຕ໋ອກທີ່ສາຂານີ້ (ຄັ້ງທຳອິດຫຼັງບັນທຶກ)
                    </p>
                  ) : null}
                </>
              )
            }
          />

          <div className="grid gap-2 sm:grid-cols-2">
            <FormSelect
              name="adjustmentType"
              label="ປະເພດ"
              options={ADJUSTMENT_OPTIONS}
            />
            <FormNumber
              name="delta"
              label="ປ່ຽນແປງ (+ ຫຼື −)"
              requiredMark
              placeholder="ຕົວຢ່າງ: 5 ຫຼື -2"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs">
              ເລືອກເຫດຜົນ
            </Label>
            <div className="flex flex-wrap gap-2">
              {REASON_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    form.setValue("adjustmentType", preset.adjustmentType, {
                      shouldValidate: true,
                    });
                    form.setValue("reason", preset.reason, {
                      shouldValidate: true,
                    });
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>
          <FormTextarea
            name="reason"
            label="ເຫດຜົນ"
            requiredMark
            placeholder="ລະບຸເຫດຜົນການປັບ..."
            rows={3}
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={adjust.isPending}>
              {adjust.isPending ? (
                <span className="inline-flex items-center gap-2">
                  <Loader />
                  ກຳລັງບັນທຶກ
                </span>
              ) : (
                "ບັນທຶກ"
              )}
            </Button>
          </div>
        </FormRoot>
      ) : null}
    </Modal>
  );
}
